import React, { useState, useRef } from 'react';

interface ListeningItem {
  audio_text: string;
  question_type: string;
  correct_answer: string;
  hint?: string;
}

interface ListeningExerciseProps {
  items: ListeningItem[];
  onSubmit: (answers: string[]) => void;
}

/**
 * ListeningExercise Component
 *
 * Users listen to audio and type what they hear (transcription).
 * Note: Actual TTS integration needed for production.
 */
const ListeningExercise: React.FC<ListeningExerciseProps> = ({ items, onSubmit }) => {
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = async (index: number, text: string) => {
    setPlayingIndex(index);

    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('access_token');

      // Use OpenAI TTS API via backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/text_to_speech/generate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          text_to_convert: text  // Backend expects 'text_to_convert' field
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate audio');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingIndex(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setPlayingIndex(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
        alert('Failed to play audio');
      };

      await audio.play();
    } catch (error) {
      console.error('TTS Error:', error);
      alert('Failed to generate audio. Please try again.');
      setPlayingIndex(null);
    }
  };

  const handleStop = () => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingIndex(null);
  };

  const handleAnswerChange = (index: number, value: string) => {
    setAnswers({
      ...answers,
      [index]: value
    });
  };

  const handleSubmit = () => {
    const answerArray = items.map((_, index) => answers[index] || '');
    onSubmit(answerArray);
  };

  const isComplete = Object.keys(answers).length === items.length &&
    Object.values(answers).every(answer => answer.trim() !== '');

  return (
    <div className="space-y-6">
      <div className="alert alert-info">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        </svg>
        <span>Listen to the audio and type what you hear</span>
      </div>

      {items.map((item, index) => {
        const isPlaying = playingIndex === index;

        return (
          <div key={index} className="card bg-base-200">
            <div className="card-body">
              <div className="flex items-center gap-4 mb-4">
                <span className="badge badge-primary">{index + 1}</span>

                {/* Play Button */}
                <button
                  onClick={() => isPlaying ? handleStop() : handlePlay(index, item.audio_text)}
                  className={`btn ${isPlaying ? 'btn-error' : 'btn-primary'} gap-2`}
                >
                  {isPlaying ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                      </svg>
                      Stop
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      Play Audio
                    </>
                  )}
                </button>

                {isPlaying && (
                  <span className="loading loading-bars loading-sm"></span>
                )}
              </div>

              {/* Answer Input */}
              <div>
                <label className="label">
                  <span className="label-text">Your answer:</span>
                </label>
                <input
                  type="text"
                  value={answers[index] || ''}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  className="input input-bordered input-primary w-full"
                  placeholder="Type what you heard..."
                />
              </div>

              {/* Hint */}
              {item.hint && (
                <div className="alert alert-warning mt-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span className="text-sm">Hint: {item.hint}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex justify-center pt-4">
        <button
          onClick={handleSubmit}
          className="btn btn-primary btn-lg"
          disabled={!isComplete}
        >
          {isComplete
            ? 'Submit Answers'
            : `Answer all items (${Object.values(answers).filter(a => a.trim()).length}/${items.length})`
          }
        </button>
      </div>
    </div>
  );
};

export default ListeningExercise;
